/* Phase 7 contract types: docs/api/phase-7-reports-gst.md */

export type ColumnType = 'text' | 'money' | 'qty' | 'pct' | 'int' | 'date' | 'status'

export interface ReportColumn {
  key: string
  label: string
  type: ColumnType
  align: 'left' | 'right'
  cost: boolean
  total: boolean
}

export interface ReportMeta {
  key: string
  title: string
  description: string
  group: string
  filters: string[]
  group_by: { key: string; label: string }[] | null
  default_group_by: string | null
  group_by_label?: string
  default_preset: string
  requires_cost: boolean
  chart: 'bar' | 'line' | 'donut' | null
  link: string | null
}

export interface ReportGroup {
  key: string
  label: string
  reports: ReportMeta[]
}

export interface ReportView {
  id: number
  report_key: string
  name: string
  filters: Record<string, string | number>
}

export interface Catalog {
  groups: ReportGroup[]
  favourites: string[]
  views: ReportView[]
  presets: Record<string, string>
}

export type Row = Record<string, string | number | null> & {
  _id?: string | number | null
  _link?: string | null
}

export interface ReportResult {
  report: ReportMeta
  heading?: string | null
  filter: Record<string, string | number> & { from: string; to: string; label: string; as_of: string }
  columns: ReportColumn[]
  rows: Row[]
  totals: Record<string, string | number>
  chart: {
    type: 'bar' | 'line' | 'donut'
    x: string
    series: { key: string; label: string }[]
    data: Row[]
  } | null
  meta: { current_page: number; last_page: number; per_page: number; total: number }
}

export interface ReportExport {
  id: number
  report_key: string
  format: 'csv' | 'xlsx' | 'pdf'
  status: 'pending' | 'done' | 'failed'
  rows: number | null
  filename: string | null
  error: string | null
  download_url: string | null
  created_at: string
  completed_at: string | null
}

export interface FilterOptions {
  salespeople: { id: number; name: string }[]
  partners: { id: number; name: string }[]
  sites: { id: number; name: string }[]
  modes: string[]
  employees: { id: number; name: string; employee_code: string }[]
}

/* ---------------------------------------------------------------- dashboard */

export interface PeriodKpi {
  today: string
  mtd: string
  ytd: string
  mtd_previous: string
  change_pct: string | null
}

export interface Dashboard {
  as_of: string
  scope: 'all' | 'own'
  kpis: {
    sales?: PeriodKpi
    collections?: PeriodKpi
    outstanding?: { total: string; overdue: string }
    gross_margin?: { mtd: string; mtd_pct: string | null }
    cash_bank?: { total: string; accounts: { id: number; name: string; type: string; balance: string }[] }
  }
  trend: { granularity: 'day'; current: TrendPoint[]; previous: TrendPoint[] } | null
  top_items: { id: number; name: string; quantity: string; value: string }[] | null
  top_customers: { id: number; name: string; value: string; invoices: number }[] | null
  partner_leaderboard: Leader[] | null
  executive_leaderboard: Leader[] | null
  ageing: Record<AgeingBucket, string> | null
  low_stock: { id: number; name: string; code: string; quantity: string; min_stock: string }[] | null
  pending: {
    discount_approvals: number | null
    partner_orders: number | null
    partner_remittances: number | null
    stock_adjustments: number | null
  }
  shift: {
    register: string | null
    opened_at: string
    sales_count: number
    gross: string
    by_mode: Record<string, string>
    expected_cash: string
  } | null
}

export interface TrendPoint {
  label: string
  date: string
  value: string
}

export interface Leader {
  id: number
  name: string
  sales: string
  invoices: number
}

export type AgeingBucket = 'not_due' | '0_30' | '31_60' | '61_90' | '90_plus'

export const AGEING_LABELS: Record<AgeingBucket, string> = {
  not_due: 'Not due',
  '0_30': '1–30 days',
  '31_60': '31–60 days',
  '61_90': '61–90 days',
  '90_plus': '90+ days',
}

/* ---------------------------------------------------------------------- GST */

export interface GstRegistration {
  id: number
  gstin: string
  state_code: string
  legal_name: string | null
  registration_type: string
  is_default: boolean
}

export interface GstFiling {
  id: number
  gst_registration_id: number
  return_type: 'gstr1' | 'gstr3b'
  return_label: string
  period: string
  status: 'filed' | 'reopened'
  arn: string | null
  notes: string | null
  filed_at: string
  filed_by: string | null
  reopened_at: string | null
  reopen_reason: string | null
}

export interface GstPeriodRow {
  period: string
  label: string
  gstr1: GstFiling | null
  gstr3b: GstFiling | null
}

export interface GstIssue {
  code: string
  severity: 'error' | 'warning'
  message: string
  document_type: string | null
  document_id: number | null
  number: string | null
  link: string | null
}

export interface GstValidation {
  errors: GstIssue[]
  warnings: GstIssue[]
  summary: { errors: number; warnings: number }
}

export interface Sums {
  txval: string
  iamt: string
  camt: string
  samt: string
  csamt: string
}

export type Gstr1Row = Partial<Sums> & Record<string, unknown>

export interface Gstr1 {
  period: string
  gstin: string
  summary: Record<string, { count: number; taxable?: string; tax?: string; cancelled?: number }>
  sections: Record<string, Gstr1Row[]>
  json: Record<string, unknown>
}

export interface Gstr3b {
  period: string
  gstin: string
  tables: {
    '3.1': Record<'a' | 'b' | 'c' | 'd' | 'e', Partial<Sums> & { label: string }>
    '3.2': { pos: string; txval: string; iamt: string }[]
    '4': {
      available: (Omit<Sums, 'txval'> & { ty: string; label: string })[]
      reversed: (Omit<Sums, 'txval'> & { ty: string; label: string })[]
      net: Omit<Sums, 'txval'>
      ineligible: (Omit<Sums, 'txval'> & { ty: string; label: string })[]
    }
  }
  json: Record<string, unknown>
}

/* ------------------------------------------------------------ subscriptions */

export type DigestKind = 'daily_summary' | 'weekly_outstanding' | 'monthly_pnl'
export type DigestChannel = 'email' | 'whatsapp' | 'telegram'

export interface Delivery {
  channel: DigestChannel | 'all'
  status: 'sent' | 'link' | 'failed' | 'skipped'
  link: string | null
  detail: string | null
  created_at: string
}

export interface Subscription {
  id: number
  kind: DigestKind
  kind_label: string
  channels: DigestChannel[]
  email: string | null
  whatsapp_phone: string | null
  telegram_chat_id: string | null
  is_active: boolean
  last_sent_at: string | null
  deliveries: Delivery[]
}

export interface SubscriptionsMeta {
  kinds: { kind: DigestKind; label: string; allowed: boolean }[]
  telegram_connected: boolean
  telegram_bot: string | null
}
